import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonTable from "../../../../components/Table/Table";
import "./DesiredTypeList.css";
import { getAllDesired, updateMetalStatusApi, deleteDesiredApi } from "../../../../services/DesiredType/DesiredType.service";
import { DesiredType } from "../../../../services/DesiredType/DesiredType.types";
import { Pencil, Eye, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

const FaqList: React.FC = () => {
  const [search, setSearch] = useState("");
  const [faq, setFaq] = useState<DesiredType[]>([]);
  const [selectedFaq, setSelectedFaq] = useState<DesiredType | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const calledOnce = useRef(false);


  const loadFaq = async () => {
    try {
      const response = await getAllDesired();
      const data = response.data || response;

      if (Array.isArray(data)) {
        const mapped: DesiredType[] = data
          .filter((c: any) => !c.isDelete) 
          .map((c: any) => ({
            id: c.id,
            name: c.name,
            description: c.description,
            isActive: c.isActive,
          }));
        setFaq(mapped);
      } else {
        console.error("Invalid faq response:", data);
        setFaq([]);
      }
    } catch (error) {
      console.error("Error fetching faq:", error);
      setFaq([]);
    }
  };

  useEffect(() => {
    if (calledOnce.current) return;
    calledOnce.current = true;
    loadFaq();
  }, []);


  const toggleStatus = async (id: number) => {
    try {
      await updateMetalStatusApi(id);

      const updated = faq.map((item) => {
        if (item.id === id) {
          const newIsActive = !item.isActive;

          return {
            ...item,
            isActive: newIsActive,
          };
        }
        return item;
      });

      setFaq(updated);
       toast.success(`Status updated successfully`);
    } catch (error) {
      alert("Failed to update status!");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteDesiredApi(id);
      setFaq((prev) => prev.filter((item) => item.id !== id));
      toast.success("Deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete");
    }
  };

  const filtered = Array.isArray(faq)
    ? faq.filter((c) =>
      c.name?.toLowerCase().includes(search.toLowerCase())
    )
    : [];


  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filtered
    .slice(startIndex, startIndex + itemsPerPage)
    .map((item, index) => ({
      ...item,
      serial: startIndex + index + 1,
    }));

  const columns = [
    { key: "serial", label: "S.NO" },
    { key: "name", label: "Name" },
    { key: "description", label: "Description" },
    {
      key: "isActive",
      label: "Status",
      render: (_: any, row: any) => (
        <button
          className={`status-btn ${row.isActive ? "active" : "inactive"}`}
          onClick={() => toggleStatus(row.id)}
        >
          {row.isActive ? "Active" : "Inactive"}
        </button>
      ),
    }
  ];

  return (
    <div className="category-container">
      <div className="header-bar">
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="search-bar"
        />
        <button className="add-btn" onClick={() => navigate("/add/desired-type")}>
          Add Desired Type
        </button>
      </div>

      {/* ✅ Table with paginated data */}
      <CommonTable
        columns={columns}
        data={paginatedData}
        tableClassName="compact-table"
        actions={(row) => (
          <div className="action-icons">
            <button
              className="icon-btn"
              title="View DesiredTypeList"
              onClick={() => setSelectedFaq(row)}
            >
              <Eye size={16} />
            </button>

            <button
              className="icon-btn"
              title="Edit EditDesiredType"
              onClick={() => navigate(`/edit/desired-type/${row.id}`)}
            >
              <Pencil size={16} />
            </button>

            <button
              className="icon-btn delete"
              title="Delete Desired Type"
              onClick={() => handleDelete(row.id)}
            >
              <Trash2 size={16} color="#ef4444" />
            </button>
          </div>
        )}
      />

      {/* ✅ Pagination Controls */}
      <div className="pagination">
        <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1} > Previous </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}> Next </button>
      </div>

      {/* ✅ Modal */}
      {selectedFaq && (
        <div className="modal-overlay" onClick={() => setSelectedFaq(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Desired Type Details</h3>
            <table className="details-table">
              <tbody>
                <tr>
                  <td><strong>ID</strong></td>
                  <td>{selectedFaq.id}</td>
                </tr>
                <tr>
                  <td><strong>Name</strong></td>
                  <td>{selectedFaq.name}</td>
                </tr>
                <tr>
                  <td><strong>Description</strong></td>
                  <td>{selectedFaq.description}</td>
                </tr>
                <tr>
                  <td><strong>Status</strong></td>
                  <td>
                    <button
                      className={`status-btn ${selectedFaq.isActive ? "active" : "inactive"}`}
                      onClick={() =>
                        setSelectedFaq({
                          ...selectedFaq,
                          isActive: !selectedFaq.isActive,
                        })
                      }
                    >
                      {selectedFaq.isActive ? "Active" : "Inactive"}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <button className="add-btn" onClick={() => setSelectedFaq(null)} style={{ marginTop: "15px" }} > Close </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaqList;
